import { Api, BASE_URL, BOTS_BASE_URL } from '@/api/index';
import axios, { AxiosRequestConfig } from 'axios';
import { LinkedInPage, LinkedInProfilePage } from 'ignite360-core';

export interface LinkedInFeedItem {
  id: string;
  title: string;
  content: string;
  mediaType: string;
  thumbnailUrl: string | null;
  permalink?: string | null;
}

export class LinkedInApi {
  static async loadLinkedInEmail(botId: string): Promise<string> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: `${BASE_URL}/public/linked-in/profile/email?bot_id=${botId}`,
    };

    const result = await axios.request(config);

    if (result.status === 200 && result.data) {
      return result.data;
    }

    throw new Error('Something went wrong while trying to load your email address.');
  }

  static async loadLinkedInProfile(botId: string): Promise<LinkedInProfilePage> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: `${BASE_URL}/public/linked-in/profile?bot_id=${botId}`,
    };

    const result = await axios.request(config);

    if (result.status === 200 && result.data) {
      return result.data as LinkedInProfilePage;
    }

    throw new Error('Something went wrong while trying to load your LinkedIn profile.');
  }

  static async loadOrganizations(botId: string): Promise<LinkedInPage[]> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: `${BASE_URL}/public/linked-in/organizations?bot_id=${botId}`,
    };

    const res = await axios.request(config);

    if (res.status === 200) {
      return res.data as LinkedInPage[];
    }

    throw new Error('Something went wrong while loading organizations!');
  }

  static async publishTestPost(botId: string) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/linked-in/publish-listing`,
    };

    const res = await axios.request(config);

    if (res.status !== 204) {
      throw new Error('Something went wrong while trying to post on LinkedIn.');
    }
  }

  static async connectBotWithLinkedInPage(botId: string, page: LinkedInPage): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/linked-in/subscribe`,
      data: { _id: page._id, type: page.type },
    };

    const res = await axios.request(config);

    if (res.status === 200) {
      return;
    }

    throw new Error(
      res.data.error || 'Something went wrong while trying to connect your LinkedIn Page.',
    );
  }

  static async disconnectBotFromLinkedInPage(botId: string): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/linked-in/unsubscribe`,
    };

    const res = await axios.request(config);

    if (res.status === 204) {
      return;
    }

    throw new Error(
      res.data.error || 'Something went wrong while trying to disconnect your LinkedIn Page.',
    );
  }

  static async getRecentPosts(botId: string): Promise<LinkedInFeedItem[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/linked-in/recent-posts`,
    });
    const result = await axios.request<LinkedInFeedItem[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  /**
   * Downloads a post's media file through the API (avoids CORS on LinkedIn CDNs) for re-publish.
   */
  static async downloadMediaFile(
    botId: string,
    postId: string,
  ): Promise<{ file: File; kind: 'image' | 'video' }> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/linked-in/posts/${encodeURIComponent(postId)}/media/file`,
      responseType: 'arraybuffer',
    });
    const result = await axios.request<ArrayBuffer>(config);
    if (result.status !== 200 || !result.data) {
      throw new Error('Could not download media.');
    }
    const contentType = (result.headers['content-type'] as string) || 'application/octet-stream';
    const exposeKind = result.headers['x-linkedin-media-kind'] as string | undefined;
    const nameMatch = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/.exec(
      (result.headers['content-disposition'] as string) || '',
    );
    const rawName = nameMatch ? decodeURIComponent(nameMatch[1] || nameMatch[2]) : '';
    const treatAsVideo = exposeKind === 'video' || contentType.startsWith('video/');
    const kind: 'image' | 'video' = treatAsVideo ? 'video' : 'image';
    const fileName = rawName || `linkedin-${postId}${kind === 'video' ? '.mp4' : '.jpg'}`;
    const blob = new Blob([result.data], { type: contentType });
    return {
      file: new File([blob], fileName, { type: contentType }),
      kind,
    };
  }

  /** Permanently deletes a LinkedIn post on the connected account. */
  static async deletePost(botId: string, postId: string): Promise<void> {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/linked-in/posts/${encodeURIComponent(postId)}`,
    });
    const result = await axios.request(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not delete LinkedIn post.');
  }
}
